import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import TrackingToolbar from '../common/TrackingToolbar';
import FilterDropdown from '../common/FilterDropdown';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import MaterialIndexColumnConfigModal, { loadVisibleColumns, saveVisibleColumns } from './MaterialIndexColumnConfigModal';
import useColumnOrder from '../../hooks/useColumnOrder';
import { applyColumnOrder } from '../../utils/columnLayout';
import MaterialIndexTrendsModal from './MaterialIndexTrendsModal';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useResizableColumns from '../../hooks/useResizableColumns';
import { useToast } from '../../hooks/useToast';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import MaterialIndexAnalyticsDashboard from './MaterialIndexAnalyticsDashboard';

const COLUMNS = [
  { key: 'uid', label: 'UID', type: 'text', width: 100 },
  { key: 'category', label: 'Category', type: 'text', width: 120 },
  { key: 'description_1', label: 'Description 1', type: 'text', width: 200 },
  { key: 'description_2', label: 'Description 2', type: 'text', width: 200 },
  { key: 'long_description', label: 'Long Description', type: 'text', width: 250 },
  { key: 'type_ottav', label: 'Type', type: 'text', width: 100 },
  { key: 'type_part_number', label: 'Type Part #', type: 'text', width: 100 },
  { key: 'part_number', label: 'Part #', type: 'text', width: 100 },
  { key: 'material', label: 'Material', type: 'text', width: 120 },
  { key: 'schedule_rating', label: 'Schedule - Rating', type: 'text', width: 120 },
  { key: 'size', label: 'Size', type: 'text', width: 80 },
  { key: 'um', label: 'UM', type: 'text', width: 60 },
  { key: 'bore', label: 'Bore', type: 'text', width: 80 },
  { key: 'mat1', label: 'Mat1', type: 'text', width: 80 },
  { key: 'matbore', label: 'Matbore', type: 'text', width: 80 },
  { key: 'manufacturing_standard', label: 'Manufacturing Standard', type: 'text', width: 150 },
  { key: 'design_code', label: 'Design Code', type: 'text', width: 100 },
  { key: 'seam_type', label: 'Seam Type', type: 'text', width: 100 },
  { key: 'end_type', label: 'End Type', type: 'text', width: 100 },
  { key: 'external_coating', label: 'External Coating', type: 'text', width: 120 },
  { key: 'bend_angle', label: 'Bend Angle', type: 'text', width: 90 },
  { key: 'radius_type', label: 'Radius Type', type: 'text', width: 100 },
  { key: 'connection_type', label: 'Connection Type', type: 'text', width: 120 },
  { key: 'facing_type', label: 'Facing Type', type: 'text', width: 100 },
  { key: 'joint_weld_to_be_reviewed', label: 'Joint/Weld Review', type: 'text', width: 120 },
  { key: 'hr', label: 'HR', type: 'number', width: 70 },
  { key: 'dia_in', label: 'Dia In', type: 'number', width: 70 },
  { key: 'material_usd', label: 'Material USD', type: 'number', width: 100 },
  { key: 'material_cad', label: 'Material CAD', type: 'number', width: 100 },
  { key: 'labour_rate_cad', label: 'Labour Rate CAD', type: 'number', width: 110 },
  { key: 'labour_rate_usd', label: 'Labour Rate USD', type: 'number', width: 110 },
  { key: 'labour_cost_cad', label: 'Labour Cost CAD', type: 'number', width: 110 },
  { key: 'labour_cost_usd', label: 'Labour Cost USD', type: 'number', width: 110 },
  { key: 'ottav_unique', label: 'Ottav Unique', type: 'text', width: 120 },
  { key: 'rating', label: 'Rating', type: 'text', width: 80 },
  { key: 'joints', label: 'Joints', type: 'text', width: 80 },
  { key: 'dia_2', label: 'Dia 2', type: 'number', width: 70 },
];

// Format value for display (numbers formatted with locale)
const formatMaterialIndexValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number') {
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return val;
};

/**
 * Virtualized Row Component for react-window v2.x
 * Props come from rowProps, plus index and style from List
 */
const VirtualRow = memo(({ index, style, rows, displayedColumns, selectedRows, onSelectRow, onDeleteRow, onSaveCell, savingRows, columnWidths, onViewTrends }) => {
  const item = rows[index];
  if (!item) return null;
  
  const isSelected = selectedRows.has(item.id);
  const isSaving = savingRows.has(item.id);
  const rowBgColor = isSaving ? '#fff3cd' : (index % 2 === 0 ? 'white' : '#f8f9fa');
  
  return (
    <div 
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: rowBgColor,
        borderBottom: '1px solid #dee2e6',
      }}
    >
      {/* Checkbox Column */}
      <div style={{ 
        width: 40, 
        minWidth: 40, 
        maxWidth: 40,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderRight: '1px solid #eee',
        backgroundColor: rowBgColor,
      }}>
        <input 
          type="checkbox" 
          checked={isSelected}
          onChange={() => onSelectRow(item.id)}
          style={{ cursor: 'pointer' }}
        />
      </div>
      
      {/* Actions Column */}
      <div style={{ 
        width: 60, 
        minWidth: 60, 
        maxWidth: 60,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '4px',
        borderRight: '1px solid #eee',
        backgroundColor: rowBgColor,
      }}>
        <button 
          onClick={() => onViewTrends(item)} 
          style={{ 
            padding: '4px 6px', 
            backgroundColor: '#0d6efd', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '0.7rem' 
          }} 
          title="View Rate Change History"
        >
          📊
        </button>
        <button 
          onClick={() => onDeleteRow(item.id)} 
          style={{ 
            padding: '4px 6px', 
            backgroundColor: '#dc3545', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '0.7rem' 
          }} 
          title="Delete"
        >
          🗑️
        </button>
      </div>
      
      {/* Data Columns */}
      {displayedColumns.map(col => {
        const colWidth = columnWidths?.[col.key] || col.width;
        // Make UID column clickable to view trends
        const isUidColumn = col.key === 'uid';
        return (
        <div 
          key={col.key}
          style={{
            width: colWidth,
            minWidth: colWidth,
            maxWidth: colWidth,
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start',
            borderRight: '1px solid #eee',
            boxSizing: 'border-box',
            overflow: 'hidden',
            fontSize: '0.85rem',
          }}
        >
          {isUidColumn ? (
            <span 
              onClick={() => onViewTrends(item)}
              style={{ 
                color: '#0d6efd', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title="Click to view rate change history"
            >
              {item[col.key] || '-'}
            </span>
          ) : (
          <EditableCell 
            value={item[col.key]} 
            row={item} 
            column={col} 
            onSave={onSaveCell} 
            disabled={false}
            formatValue={formatMaterialIndexValue}
            accentColor="#6c757d"
          />
          )}
        </div>
      );
      })}
    </div>
  );
}, (prev, next) => {
  // Custom equality check for performance
  return (
    prev.index === next.index &&
    prev.style === next.style &&
    prev.rows === next.rows &&
    prev.displayedColumns === next.displayedColumns &&
    prev.selectedRows === next.selectedRows &&
    prev.savingRows === next.savingRows &&
    prev.columnWidths === next.columnWidths
  );
});

const MaterialIndexLog = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());

  const fetchItemsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('materialIndex', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Pagination state (1000 rows per page for performance)
  const PAGE_SIZE = 1000;
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Cache ref to avoid re-fetching same data
  const cacheRef = useRef({ data: null, timestamp: null, cacheKey: null });
  const CACHE_DURATION_MS = 60000; // 1 minute cache

  // Refs
  const mainListRef = useRef(null);

  // Resizable columns
  const defaultWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  }, []);
  const { columnWidths, getColumnWidth, getResizeHandleProps, resizingColumn } = useResizableColumns(
    'materialIndex_columnWidths',
    defaultWidths
  );

  // Export/Import State
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [materialIndexExportJobId, setMaterialIndexExportJobId] = useState(null);
  const [materialIndexExporting, setMaterialIndexExporting] = useState(false);
  const [exportType, setExportType] = useState('dashboard'); // 'dashboard' | 'editable' | 'all'
  const downloadedJobsRef = useRef(new Set());

  // Column Config State
  const [showColumnConfigModal, setShowColumnConfigModal] = useState(false);
  const [columnMappings, setColumnMappings] = useState([]);
  
  // Trends Modal State
  const [showTrendsModal, setShowTrendsModal] = useState(false);
  const [selectedRecordForTrends, setSelectedRecordForTrends] = useState(null);
  
  // Build full column list including UDF columns with custom labels
  const allColumns = useMemo(() => {
    const cols = [...COLUMNS];
    columnMappings.forEach(mapping => {
      cols.push({
        key: mapping.column_key,
        label: mapping.custom_name,
        type: 'text',
        width: 120,
        isUdf: true,
        isEditable: mapping.is_editable
      });
    });
    return cols;
  }, [columnMappings]);

  const [visibleColumns, setVisibleColumns] = useState(() => loadVisibleColumns(COLUMNS.map(c => c.key)));
  const { columnOrder, setColumnOrder } = useColumnOrder('material-index-column-order', allColumns);

  // Get visible columns for rendering
  const displayedColumns = useMemo(() => 
    applyColumnOrder(allColumns, columnOrder).filter(col => visibleColumns.has(col.key)),
    [allColumns, visibleColumns, columnOrder]
  );

  // Calculate total width for the table
  const totalTableWidth = useMemo(() => {
    const checkboxWidth = 40;
    const actionsWidth = 60;
    const columnsWidth = displayedColumns.reduce((sum, col) => sum + col.width, 0);
    return checkboxWidth + actionsWidth + columnsWidth;
  }, [displayedColumns]);

  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: allColumns, items });
  const sortHook = useTableSort({ columns: allColumns });

  // Keyboard shortcuts: Escape closes filter, Backspace goes back
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isTyping = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable;
      
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
      
      if (e.key === 'Backspace' && !isTyping) {
        e.preventDefault();
        window.history.back();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook]);

  // Load column mappings on mount
  const loadColumnMappings = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/ottaviano/column-mappings');
      if (response.data.success) {
        setColumnMappings(response.data.data || []);
        const mappedKeys = (response.data.data || []).map(m => m.column_key);
        setVisibleColumns(prev => {
          const newSet = new Set(prev);
          mappedKeys.forEach(key => newSet.add(key));
          return newSet;
        });
      }
    } catch (err) {
      console.error('Error loading column mappings:', err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    loadColumnMappings();
  }, [loadColumnMappings]);

  const handleSaveColumnConfig = (newVisibleColumns, order) => {
    setVisibleColumns(newVisibleColumns);
    saveVisibleColumns(newVisibleColumns);
    if (order) setColumnOrder(order);
    showToast(`Column configuration saved! ${newVisibleColumns.size} columns visible.`, 'success');
  };

  const handleMappingsChange = (newMappings) => {
    setColumnMappings(newMappings);
    const mappedKeys = newMappings.map(m => m.column_key);
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      mappedKeys.forEach(key => newSet.add(key));
      return newSet;
    });
  };

  // Initial fetch with pagination (first 1000 rows)
  const fetchItems = useCallback(async (reset = true) => {
    try {
      // Check cache first (only for initial load)
      const cacheKey = 'materialIndex';
      const now = Date.now();
      if (reset && cacheRef.current.data && cacheRef.current.cacheKey === cacheKey && 
          (now - cacheRef.current.timestamp) < CACHE_DURATION_MS) {
        console.log('Using cached Material Index data');
        setItems(cacheRef.current.data.items);
        setTotalCount(cacheRef.current.data.totalCount);
        setOffset(cacheRef.current.data.offset);
        setHasMore(cacheRef.current.data.hasMore);
        setLoading(false);
        return;
      }
      
      if (reset) {
        setLoading(true);
        setOffset(0);
        setItems([]);
      }
      setError(null);
      
      const currentOffset = reset ? 0 : offset;
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/ottaviano?limit=${PAGE_SIZE}&offset=${currentOffset}`);
      
      if (response.data.success) {
        const newItems = response.data.data || [];
        const total = response.data.totalCount || newItems.length;
        
        if (reset) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }
        
        setTotalCount(total);
        const newOffset = currentOffset + newItems.length;
        setOffset(newOffset);
        setHasMore(newOffset < total);
        
        // Update cache
        if (reset) {
          cacheRef.current = {
            data: { items: newItems, totalCount: total, offset: newOffset, hasMore: newOffset < total },
            timestamp: now,
            cacheKey
          };
        }
      } else {
        setError('Failed to load Material Index data');
      }
    } catch (err) { 
      console.error('Error:', err); 
      setError('Failed to load data. Please try again.'); 
    }
    finally { setLoading(false); }
  }, [instance, accounts, offset]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  // Load more function for lazy loading
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    
    try {
      setLoadingMore(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/ottaviano?limit=${PAGE_SIZE}&offset=${offset}`);
      
      if (response.data.success) {
        const newItems = response.data.data || [];
        setItems(prev => [...prev, ...newItems]);
        
        const newOffset = offset + newItems.length;
        setOffset(newOffset);
        setHasMore(newOffset < totalCount);
        
        // Update cache with all loaded data
        cacheRef.current = {
          data: { 
            items: [...items, ...newItems], 
            totalCount, 
            offset: newOffset, 
            hasMore: newOffset < totalCount 
          },
          timestamp: Date.now(),
          cacheKey: 'materialIndex'
        };
      }
    } catch (err) {
      console.error('Error loading more:', err);
      showToast('Failed to load more data', 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [instance, accounts, offset, hasMore, loading, loadingMore, totalCount, items, showToast]);

  // Force refresh (bypass cache)
  const forceRefresh = useCallback(() => {
    cacheRef.current = { data: null, timestamp: null, cacheKey: null };
    fetchItems(true);
  }, [fetchItems]);

  useEffect(() => { fetchItems(true); }, []);


  const handleSaveCell = useCallback(async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));
    try {
      const col = COLUMNS.find(c => c.key === columnKey);
      const processedValue = col?.type === 'number' ? (newValue === '' ? null : Number(newValue)) : newValue;
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/ottaviano/${rowId}`, { [columnKey]: processedValue }, syncHeaders());
      if (response.data.success) { 
        setItems(prev => prev.map(item => item.id === rowId ? { ...item, [columnKey]: processedValue } : item)); 
        showToast('Saved successfully'); 
      }
      else throw new Error(response.data.message || 'Save failed');
    } catch (err) { showToast(`Save failed: ${err.message}`, 'error'); throw err; }
    finally { setSavingRows(prev => { const s = new Set(prev); s.delete(rowId); return s; }); }
  }, [instance, accounts, showToast]);

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/ottaviano', newRow, syncHeaders());
      if (response.data.success) { 
        setItems(prev => [response.data.data, ...prev]); 
        setNewRow({}); 
        setIsAddingNew(false); 
        showToast('New record added'); 
      }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Failed: ${err.message}`, 'error'); }
  };

  const handleDeleteRow = useCallback(async (rowId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/ottaviano/${rowId}`, null, syncHeaders());
      if (response.data.success) { 
        setItems(prev => prev.filter(item => item.id !== rowId)); 
        showToast('Deleted'); 
      }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); }
  }, [instance, accounts, showToast]);

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedRows.size} selected record(s)?`)) return;

    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/ottaviano/delete-batch', {
        ids: Array.from(selectedRows)
      }, syncHeaders());

      if (response.data.success) {
        setItems(prev => prev.filter(item => !selectedRows.has(item.id)));
        setSelectedRows(new Set());
        showToast(response.data.message);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Bulk delete failed: ${err.message}`, 'error');
    }
  };

  const handleSelectRow = useCallback((rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // ============================================
  // Export Logic
  // ============================================
  const triggerDownload = async (jobId) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/ottaviano/download/${jobId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenResponse.idToken}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `material_index_export.xlsx`;

      if (contentDisposition) {
        const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        if (rfc5987Match && rfc5987Match[1]) {
          filename = decodeURIComponent(rfc5987Match[1]);
        } else {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast(`Error downloading file: ${error.message}`, 'error');
    }
  };

  const exportMaterialIndex = async (type = exportType) => {
    if (materialIndexExporting) return;
    try {
      setMaterialIndexExporting(true);
      setShowExportModal(false);
      
      // Get columns based on export type
      let columnsToExport = [];
      if (type === 'dashboard') {
        // Only currently visible dashboard columns
        columnsToExport = Array.from(visibleColumns);
      } else if (type === 'editable') {
        // All editable columns (excluding calculated fields, but no UDFs)
        columnsToExport = COLUMNS.map(c => c.key);
      } else {
        // All columns including UDFs
        columnsToExport = allColumns.map(c => c.key);
      }
      
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/ottaviano/export', {
        exportType: type,
        columns: columnsToExport
      }, syncHeaders());
      if (response.data.success) {
        setMaterialIndexExportJobId(response.data.jobId);
        showToast(`Material Index export started! Job ID: ${response.data.jobId}`, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to start export job');
      }
    } catch (error) {
      setMaterialIndexExporting(false);
      showToast(`Error exporting Material Index: ${error.message}`, 'error');
    }
  };

  const handleMaterialIndexExportJobComplete = (jobData) => {
    showToast(`Material Index export completed! ${jobData.processed_rows} records exported.`, 'success');
    setMaterialIndexExporting(false);
    setMaterialIndexExportJobId(null);
    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      triggerDownload(jobData.job_id);
    }
  };

  const handleMaterialIndexExportJobError = (errorMessage) => {
    showToast(`Material Index export failed: ${errorMessage}`, 'error');
    setMaterialIndexExporting(false);
    setMaterialIndexExportJobId(null);
  };

  // ============================================
  // Upload Logic
  // ============================================
  const handleUploadSuccess = (data) => {
    showToast(`Material Index upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success');
    forceRefresh(); // Bypass cache after upload
  };

  const handleUploadError = (message) => {
    showToast(`Material Index upload failed: ${message}`, 'error');
  };

  // ============================================
  // Filter & Sort Logic using hooks
  // ============================================
  
  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search and sort
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  // Filtered and sorted items
  // Note: Explicitly depend on filter/sort state to ensure re-computation when filters change
  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Global search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item => allColumns.some(col => { 
        const val = item[col.key]; 
        return val !== null && val !== undefined && String(val).toLowerCase().includes(search); 
      }));
    }
    
    // Apply column filters
    result = filterHook.applyFilters(result);
    
    // Apply sorting
    result = sortHook.applySorting(result);
    
    return result;
  }, [items, searchTerm, allColumns, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  // Update handleSelectAll to use filteredItems
  const handleSelectAllFiltered = useCallback((e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(filteredItems.map(item => item.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [filteredItems]);

  // Handler to view trends for a record
  const handleViewTrends = useCallback((record) => {
    setSelectedRecordForTrends({
      id: record.id,
      uid: record.uid,
      part_number: record.part_number,
      category: record.category,
      type_ottav: record.type_ottav,
      description_1: record.description_1
    });
    setShowTrendsModal(true);
  }, []);

  // Memoized row props for react-window v2.x API
  const rowProps = useMemo(() => ({
    rows: filteredItems,
    displayedColumns,
    selectedRows,
    onSelectRow: handleSelectRow,
    onDeleteRow: handleDeleteRow,
    onSaveCell: handleSaveCell,
    onViewTrends: handleViewTrends,
    savingRows,
    columnWidths,
  }), [filteredItems, displayedColumns, selectedRows, handleSelectRow, handleDeleteRow, handleSaveCell, handleViewTrends, savingRows, columnWidths]);

  // Loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ 
          display: 'inline-block', 
          width: '48px', 
          height: '48px', 
          border: '4px solid #f3f3f3', 
          borderTop: '4px solid #6c757d', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <p style={{ marginTop: '1rem', color: '#666' }}>Loading Material Index data...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404' }}>
        <strong>⚠️ Error:</strong> {error}
        <button onClick={forceRefresh} style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  const tableHeight = Math.min(Math.max(400, window.innerHeight - 450), 600);
  const headerHeight = 40;
  const rowHeight = 36;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {toast.show && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          padding: '1rem 1.5rem', 
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', 
          color: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
          zIndex: 9999 
        }}>
          {toast.message}
        </div>
      )}

      {materialIndexExportJobId && (
        <JobStatusMonitor
          jobId={materialIndexExportJobId}
          onComplete={handleMaterialIndexExportJobComplete}
          onError={handleMaterialIndexExportJobError}
        />
      )}

      {/* Toolbar using reusable component */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px', 
        marginBottom: '1rem', 
        flexWrap: 'wrap', 
        gap: '1rem' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#333' }}>📊 Material Index</h3>
          <span style={{ backgroundColor: '#6c757d', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem' }}>
            {filteredItems.length} records
          </span>
          {displayedColumns.length < allColumns.length && (
            <span style={{ backgroundColor: '#17a2b8', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem' }}>
              {displayedColumns.length}/{allColumns.length} columns
            </span>
          )}
          {columnMappings.length > 0 && (
            <span style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem' }}>
              {columnMappings.length} custom
            </span>
          )}
          {/* Inline Active Filters */}
          {hasActiveFilters && (
            <>
              <span style={{ color: '#666', fontSize: '0.8rem' }}>|</span>
              <span style={{ color: '#0056b3', fontSize: '0.8rem', fontWeight: 'bold' }}>🔍</span>
              {searchTerm && (
                <span style={{ backgroundColor: '#007bff', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '0.85rem', lineHeight: 1 }}>×</button>
                </span>
              )}
              {filterHook.activeFilters.map((f, idx) => (
                <span key={idx} style={{ backgroundColor: f.type === 'text' ? '#28a745' : f.type === 'numeric' ? '#fd7e14' : f.type === 'date' ? '#6f42c1' : '#6c757d', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {f.label}: {f.type === 'text' ? `${f.mode} "${f.value}"` : f.type === 'values' ? `${f.count} selected` : f.description || f.value}
                  <button onClick={() => filterHook.clearColumn(f.key)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '0.85rem', lineHeight: 1 }}>×</button>
                </span>
              ))}
              <button onClick={handleClearAll} style={{ padding: '2px 8px', backgroundColor: '#065f46', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}>
                ✕ Clear All
              </button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              style={{ padding: '8px 12px 8px 36px', border: '1px solid #dee2e6', borderRadius: '4px', width: '200px', fontSize: '0.9rem' }} 
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span>
          </div>
          {selectedRows.size > 0 && (
            <button onClick={handleBulkDelete} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold' }}>
              🗑️ Delete ({selectedRows.size})
            </button>
          )}
          <button onClick={() => setShowAnalytics(true)} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>📊 Analytics</button>
          <button onClick={() => setShowColumnConfigModal(true)} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>⚙️ Column Config</button>
          <button onClick={() => setShowExportModal(true)} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>📤 Export</button>
          <button onClick={() => setShowUploadModal(true)} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>📥 Import</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>➕ Add New</button>
          <button onClick={forceRefresh} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem' }}>🔄 Refresh</button>
        </div>
      </div>


      {/* Tip */}
      <div style={{ backgroundColor: '#fff3e0', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem', color: '#e65100' }}>
        💡 <strong>Tip:</strong> Click any cell to edit. Check boxes to select multiple rows for deletion. 
        <strong>Import Deletion:</strong> Export data, type "d" into the "action" column for rows to delete, then re-import.
      </div>

      {/* Add New Row Form */}
      {isAddingNew && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #dee2e6' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Add New Material Index Record</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {COLUMNS.slice(0, 12).map(col => (
              <div key={col.key}>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>{col.label}</label>
                <input 
                  type={col.type === 'number' ? 'number' : 'text'} 
                  step="0.01" 
                  value={newRow[col.key] || ''} 
                  onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))} 
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }} 
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleAddNewRow} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✓ Save</button>
            <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* Virtualized Table */}
      <div 
        className="virtualized-table-container" 
        style={{ 
          flex: 1, 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          backgroundColor: 'white',
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <div style={{ width: 'fit-content', minWidth: totalTableWidth }}>
          {/* Header Row */}
          <div style={{ 
            display: 'flex', 
            backgroundColor: '#f8f9fa', 
            color: '#495057',
            position: 'sticky', 
            top: 0, 
            zIndex: 10,
            height: headerHeight,
            borderBottom: '1px solid #dee2e6',
          }}>
            {/* Checkbox Header */}
            <div style={{ 
              width: 40, 
              minWidth: 40, 
              maxWidth: 40,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRight: '1px solid #dee2e6',
            }}>
              <input 
                type="checkbox" 
                checked={selectedRows.size > 0 && selectedRows.size === filteredItems.length}
                onChange={handleSelectAllFiltered}
                title="Select all"
                style={{ cursor: 'pointer' }}
              />
            </div>
            
            {/* Actions Header */}
            <div style={{ 
              width: 60, 
              minWidth: 60, 
              maxWidth: 60,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRight: '1px solid #dee2e6',
              fontSize: '0.85rem',
              fontWeight: 'bold',
            }}>
              Actions
            </div>
            
            {/* Column Headers */}
            {displayedColumns.map(col => {
              const colWidth = getColumnWidth(col.key, col.width);
              const hasFilter = filterHook.hasColumnFilter(col.key);
              return (
              <div 
                key={col.key}
                style={{
                  width: colWidth,
                  minWidth: colWidth,
                  maxWidth: colWidth,
                  padding: '8px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start',
                  borderRight: '1px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                  position: 'relative',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                }}
              >
                <span 
                  onClick={() => sortHook.handleSort(col.key)} 
                  style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={`Click to sort by ${col.label}`}
                >
                  {col.label}
                  {sortHook.sortColumn === col.key && (
                    <span style={{ marginLeft: '4px' }}>
                      {sortHook.sortDirection === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </span>
                <span 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    filterHook.setFilterSearch(''); 
                    filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); 
                  }} 
                  style={{ 
                    cursor: 'pointer', 
                    opacity: hasFilter ? 1 : 0.5, 
                    fontSize: '0.75rem',
                    marginLeft: '4px',
                  }} 
                  title="Filter"
                >
                  🔽
                </span>
                {/* Resize Handle */}
                <div {...getResizeHandleProps(col.key, colWidth)} />
                
                {/* Filter Dropdown */}
                {filterHook.openFilterColumn === col.key && (
                  <FilterDropdown
                    column={col}
                    uniqueValues={filterHook.getUniqueValues(col.key)}
                    selectedValues={filterHook.selectedValues[col.key] || []}
                    textFilter={filterHook.textFilters[col.key] || null}
                    numericFilter={filterHook.numericFilters[col.key] || null}
                    dateFilter={filterHook.dateFilters[col.key] || null}
                    filterSearch={filterHook.filterSearch}
                    onToggleValue={filterHook.toggleValue}
                    onSelectAll={filterHook.selectAll}
                    onClearColumn={filterHook.clearColumn}
                    onApplyTextFilter={filterHook.applyTextFilter}
                    onApplyNumericFilter={filterHook.applyNumericFilter}
                    onApplyDateFilter={filterHook.applyDateFilter}
                    onClose={() => {
                      filterHook.setOpenFilterColumn(null);
                      filterHook.setFilterSearch('');
                    }}
                    onSearchChange={filterHook.setFilterSearch}
                  />
                )}
              </div>
            );
            })}
          </div>
          
          {/* Virtualized Body */}
          <div ref={mainListRef} style={{ height: tableHeight }}>
            {filteredItems.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                {searchTerm ? 'No matching records' : 'No records. Click "Add New" to create one.'}
              </div>
            ) : (
              <List
                key={`${filteredItems.length}-${Object.keys(filterHook.selectedValues).length}-${Object.keys(filterHook.textFilters).length}-${Object.keys(filterHook.numericFilters).length}`}
                rowComponent={VirtualRow}
                rowCount={filteredItems.length}
                rowHeight={rowHeight}
                rowProps={rowProps}
                overscanCount={10}
                style={{ height: tableHeight, width: '100%', overflowX: 'hidden' }}
              />
            )}
          </div>
          
          {/* Load More / Pagination Status */}
          <div style={{ 
            padding: '0.75rem 1rem', 
            backgroundColor: '#f8f9fa', 
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.85rem',
            color: '#495057'
          }}>
            <div>
              <strong>Loaded:</strong> {items.length.toLocaleString()} of {totalCount.toLocaleString()} records
              {items.length !== filteredItems.length && (
                <span style={{ marginLeft: '0.5rem', color: '#6c757d' }}>
                  (Showing {filteredItems.length.toLocaleString()} after filters)
                </span>
              )}
            </div>
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: loadingMore ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {loadingMore ? (
                  <>
                    <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    Loading...
                  </>
                ) : (
                  <>⬇️ Load More ({Math.min(PAGE_SIZE, totalCount - items.length).toLocaleString()} more)</>
                )}
              </button>
            )}
            {!hasMore && items.length > 0 && (
              <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ All records loaded</span>
            )}
          </div>
        </div>
      </div>

      {/* Material Index Export Modal with 3 Options */}
      {showExportModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '2rem',
            width: '90%', maxWidth: '550px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: '#333' }}>📊 Export Material Index</h3>
              <button onClick={() => setShowExportModal(false)} disabled={materialIndexExporting}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Choose which columns to include in the export:
            </p>
            
            {/* Export Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', padding: '1rem',
                border: exportType === 'dashboard' ? '2px solid #28a745' : '1px solid #dee2e6',
                borderRadius: '6px', cursor: 'pointer', backgroundColor: exportType === 'dashboard' ? '#f0fff4' : 'white'
              }}>
                <input type="radio" name="exportType" value="dashboard" checked={exportType === 'dashboard'}
                  onChange={() => setExportType('dashboard')} style={{ marginRight: '0.75rem' }} />
                <div>
                  <strong style={{ color: '#333' }}>📋 Dashboard Columns Only</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Export only the columns currently visible in the dashboard ({visibleColumns.size} columns)
                  </p>
                </div>
              </label>
              
              <label style={{
                display: 'flex', alignItems: 'center', padding: '1rem',
                border: exportType === 'editable' ? '2px solid #28a745' : '1px solid #dee2e6',
                borderRadius: '6px', cursor: 'pointer', backgroundColor: exportType === 'editable' ? '#f0fff4' : 'white'
              }}>
                <input type="radio" name="exportType" value="editable" checked={exportType === 'editable'}
                  onChange={() => setExportType('editable')} style={{ marginRight: '0.75rem' }} />
                <div>
                  <strong style={{ color: '#333' }}>📝 All Standard Columns</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Export all standard columns except UDFs ({COLUMNS.length} columns)
                  </p>
                </div>
              </label>
              
              <label style={{
                display: 'flex', alignItems: 'center', padding: '1rem',
                border: exportType === 'all' ? '2px solid #28a745' : '1px solid #dee2e6',
                borderRadius: '6px', cursor: 'pointer', backgroundColor: exportType === 'all' ? '#f0fff4' : 'white'
              }}>
                <input type="radio" name="exportType" value="all" checked={exportType === 'all'}
                  onChange={() => setExportType('all')} style={{ marginRight: '0.75rem' }} />
                <div>
                  <strong style={{ color: '#333' }}>📦 All Columns Including UDFs</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Export all columns including custom UDF columns ({allColumns.length} columns)
                  </p>
                </div>
              </label>
            </div>
            
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowExportModal(false)} disabled={materialIndexExporting}
                style={{ padding: '0.75rem 1.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => exportMaterialIndex(exportType)} disabled={materialIndexExporting}
                style={{ padding: '0.75rem 1.5rem', backgroundColor: materialIndexExporting ? '#6c757d' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {materialIndexExporting ? (
                  <>
                    <div style={{ width: 16, height: 16, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    Exporting...
                  </>
                ) : '📤 Export to Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
        config={UPLOAD_CONFIGS.MATERIAL_INDEX}
      />

      <MaterialIndexColumnConfigModal
        isOpen={showColumnConfigModal}
        onClose={() => setShowColumnConfigModal(false)}
        columns={COLUMNS}
        visibleColumns={visibleColumns}
        columnOrder={columnOrder}
        onSave={handleSaveColumnConfig}
        onMappingsChange={handleMappingsChange}
      />

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <MaterialIndexAnalyticsDashboard
          data={items}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* Rate Trends Modal */}
      <MaterialIndexTrendsModal
        isOpen={showTrendsModal}
        onClose={() => {
          setShowTrendsModal(false);
          setSelectedRecordForTrends(null);
        }}
        recordId={selectedRecordForTrends?.id}
        recordInfo={selectedRecordForTrends}
      />

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .virtualized-table-container { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
        .virtualized-table-container::-webkit-scrollbar { width: 12px; height: 12px; }
        .virtualized-table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
        .virtualized-table-container::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
      `}</style>
    </div>
  );
};

export default MaterialIndexLog;
